/**
 * Header de navegación vertical.
 *
 * Cuatro responsabilidades: abrir/cerrar el panel de catálogo (con trampa de
 * foco), conmutar entre superpuesto y sólido al hacer scroll, esconderse al bajar
 * y volver al subir, y mantener el contador de la cesta sincronizado.
 *
 * El scroll no siempre es de la ventana. Fabric, desde 990px, fija html y body a
 * la altura de la pantalla y hace que se desplace .page-wrapper; en móvil se
 * desplaza la ventana. Antes sólo se escuchaba la ventana: en escritorio el
 * header nunca se enteraba de que se había bajado, y en la portada se quedaba
 * transparente y con tinta blanca encima de secciones blancas. Ahora se escuchan
 * los dos y se toma el que se esté moviendo.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class ScHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[ref="toggle"]');
    this.panel = this.querySelector('[ref="panel"]');
    this.closeBtn = this.querySelector('[ref="close"]');

    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onCartChange = this.onCartChange.bind(this);

    this.toggle?.addEventListener('click', this.onToggle);
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', this.onKeydown);

    /* Fabric calcula --header-height buscando un <header-component>, que este
       header no usa. Sin ese valor, los paneles sticky se apoyan en un número
       inventado. Se mide la fila y la barra de categorías, no el elemento
       entero: el panel de menú es fixed y no debe contar. La barra sí, porque
       queda pegada arriba junto al logo y lo que se ancla debajo —la columna
       de la ficha, el resumen del carrito— se metería bajo ella. */
    this.row = this.querySelector('.sc-header__row');
    this.cats = this.querySelector('[data-cats]');
    this.headerHeight = 80;
    this.measure = this.measure.bind(this);
    this.measure();

    if ('ResizeObserver' in window && this.row) {
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.row);
      if (this.cats) this.resizeObserver.observe(this.cats);
    }

    this.setupSlides();

    // passive: el handler sólo lee la posición y conmuta atributos.
    this.pageWrapper = document.querySelector('.page-wrapper');
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.pageWrapper?.addEventListener('scroll', this.onScroll, { passive: true });
    this.lastY = this.scrollPosition();
    this.update();

    // Quien navega con teclado y llega al header escondido tiene que verlo.
    this.onFocusIn = () => this.reveal();
    this.addEventListener('focusin', this.onFocusIn);

    // Fabric emite cart:update; el resto de temas usan variantes. Escuchamos
    // ambas y caemos a un fetch del carrito como red de seguridad.
    document.addEventListener('cart:update', this.onCartChange);
    document.addEventListener('cart:refresh', this.onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll);
    this.pageWrapper?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('focusin', this.onFocusIn);
    document.removeEventListener('cart:update', this.onCartChange);
    document.removeEventListener('cart:refresh', this.onCartChange);
    this.resizeObserver?.disconnect();
    this.stopSlides();
  }

  measure() {
    if (!this.row) return;
    // offsetHeight es 0 si la barra está oculta (desactivada en móvil): suma nada.
    this.headerHeight = this.row.offsetHeight + (this.cats?.offsetHeight || 0);
    document.body.style.setProperty('--header-height', `${this.headerHeight}px`);
  }

  /**
   * Pase de imágenes del panel de catálogo.
   *
   * Sólo corre mientras el panel está abierto: rotar un carrusel que nadie ve
   * es trabajo tirado, y en móvil además gasta batería. Se para al cerrar.
   */
  setupSlides() {
    const wrap = this.querySelector('[data-slides]');
    if (!wrap) return;

    this.slides = [...wrap.querySelectorAll('.sc-menu__slide')];
    if (this.slides.length < 2) {
      this.slides = null;
      return;
    }

    // Con movimiento reducido no se rota sola, pero sí se elige al azar cuál se
    // queda: quien pide menos animación no está pidiendo ver siempre la misma
    // prenda.
    this.slidesStill = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slideOrder = this.slides.map((_, i) => i);
    this.slidePos = 0;
    this.slideInterval = Number(wrap.dataset.interval) || 6000;
  }

  /* Orden nuevo en cada apertura.
   *
   * En orden de colección, abrir el menú enseñaba siempre la misma primera
   * pieza y las últimas no se veían nunca. Se baraja entero —no sólo el punto
   * de partida— para que dos aperturas seguidas tampoco coincidan en la
   * secuencia, y se evita empezar por la que quedó a la vista la última vez. */
  shuffleSlides() {
    const current = this.slideOrder ? this.slideOrder[this.slidePos] : -1;
    const order = this.slides.map((_, i) => i);

    for (let i = order.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }

    if (order[0] === current && order.length > 1) {
      [order[0], order[1]] = [order[1], order[0]];
    }

    this.slideOrder = order;
    this.slidePos = 0;
  }

  showSlide(index) {
    for (const [i, slide] of this.slides.entries()) {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
    }

    // La que se ve y la siguiente dejan de ser diferidas. Con un orden fijo la
    // primera imagen era siempre la misma y llegaba a estar en caché; al
    // sortearla, cada apertura estrena una foto y sin esto entraría en blanco.
    for (const pos of [this.slidePos, (this.slidePos + 1) % this.slideOrder.length]) {
      const img = this.slides[this.slideOrder[pos]]?.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }
  }

  startSlides() {
    if (!this.slides) return;

    this.shuffleSlides();
    this.showSlide(this.slideOrder[0]);

    if (this.slidesStill || this.slideTimer) return;

    this.slideTimer = setInterval(() => {
      this.slidePos = (this.slidePos + 1) % this.slideOrder.length;
      this.showSlide(this.slideOrder[this.slidePos]);
    }, this.slideInterval);
  }

  stopSlides() {
    clearInterval(this.slideTimer);
    this.slideTimer = null;
  }

  get isOpen() {
    return this.panel && !this.panel.hasAttribute('hidden');
  }

  onToggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (!this.panel) return;
    this.previouslyFocused = document.activeElement;
    // El panel es fixed y vive dentro del header: si el header estuviera
    // desplazado con transform, el panel se desplazaría con él. Abierto, manda.
    this.setAttribute('data-menu-open', '');
    this.reveal();
    this.panel.removeAttribute('hidden');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';

    // El primer elemento enfocable del panel, no el panel en sí.
    this.panel.querySelector(FOCUSABLE)?.focus();

    this.startSlides();
  }

  close() {
    if (!this.panel) return;
    this.panel.setAttribute('hidden', '');
    this.removeAttribute('data-menu-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    this.previouslyFocused?.focus();

    this.stopSlides();
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Trampa de foco: el panel es fixed a pantalla completa, así que dejar el
    // foco salir hacia la página de debajo lo dejaría inaccesible.
    const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null
    );
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  scrollPosition() {
    return Math.max(window.scrollY || 0, this.pageWrapper?.scrollTop || 0);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  /**
   * Esconder al bajar, volver al subir.
   *
   * Leer la página pide pantalla; buscar algo pide el menú. Bajando, el header
   * se aparta; en cuanto se sube un poco, vuelve, sin obligar a regresar hasta
   * arriba para abrir el menú o la búsqueda. La tolerancia evita que tiemble
   * con el rebote del trackpad o del dedo. Nunca se esconde mientras la foto de
   * cabecera todavía lo cubre, con el menú abierto o con el foco dentro.
   */
  update() {
    const y = this.scrollPosition();
    const delta = y - this.lastY;
    this.lastY = y;

    // Umbral corto: en cuanto el hero deja de cubrir el header, necesita fondo.
    this.toggleAttribute('data-scrolled', y > 24);

    const TOLERANCE = 6;
    const headerHeight = this.headerHeight || 80;

    if (this.isOpen || y <= headerHeight || this.contains(document.activeElement)) {
      this.reveal();
    } else if (delta > TOLERANCE) {
      this.setAttribute('data-hidden', '');
    } else if (delta < -TOLERANCE) {
      this.reveal();
    }
  }

  reveal() {
    this.removeAttribute('data-hidden');
  }

  async onCartChange(event) {
    const target = this.querySelector('[data-cart-count]');
    if (!target) return;

    let count = event?.detail?.cart?.item_count ?? event?.detail?.item_count;

    if (typeof count !== 'number') {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        if (!res.ok) return;
        count = (await res.json()).item_count;
      } catch {
        return; // Sin red: dejamos el contador como está antes que mostrar un cero falso.
      }
    }

    // La burbuja desaparece en cero. Un 0 permanente sobre la bolsa es ruido, y
    // dejaría al header contradiciendo al botón flotante, que con la cesta
    // vacía tampoco existe.
    target.textContent = count;
    target.hidden = count === 0;

    const label = this.querySelector('[data-cart-label]');
    if (label) label.textContent = `${count} artículos`;
  }
}

if (!customElements.get('sc-header')) {
  customElements.define('sc-header', ScHeader);
}
